import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  Visibility as ViewIcon,
  PictureAsPdf as PdfIcon,
} from "@mui/icons-material";
import customFetch from "hooks/fetchWrapper";
import { toast } from "react-toastify";
import moment from "moment";
import StudentDetailsDialog from "./StudentDetailsDialog";

const StudentManagement = () => {
  const [students, setStudents] = useState([]);
  console.log("🚀 ~ StudentManagement ~ students:", students);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  console.log("🚀 ~ StudentManagement ~ selectedStudent:", selectedStudent);
  const [openViewDialog, setOpenViewDialog] = useState(false);

  const [statusFilter, setStatusFilter] = useState("all");

  // Configurable columns
  const columns = [
    {
      field: "StudentDetails.Session",
      headerName: "Session",
      width: 100,
      editable: false,
      valueGetter: (params) =>
        params.row.schoolAcademicYear ||
        params.row.Session ||
        params.row.StudentDetails?.Session ||
        "",
    },
    {
      field: "RollNo",
      headerName: "Roll No",
      width: 100,
      editable: false,
      valueGetter: (params) =>
        params.row.admissionNo ||
        params.row.RollNo ||
        params.row.rollNo ||
        "",
    },
    {
      field: "StudentDetails.MemSName",
      headerName: "Student Name",
      width: 200,
      editable: false,
      valueGetter: (params) =>
        params.row.studentName ||
        params.row.StudentName ||
        params.row.MemFName ||
        params.row.MemSName ||
        params.row.StudentDetails?.MemFName ||
        params.row.StudentDetails?.MemSName ||
        params.row.StudentDetails?.StudentName ||
        params.row.StudentDetails?.studentName ||
        "",
    },
    {
      field: "StudentDetails.classname",
      headerName: "Class",
      width: 80,
      editable: false,
      valueGetter: (params) =>
        params.row.grade ||
        params.row.classname ||
        params.row.Class ||
        params.row.StudentDetails?.classname ||
        "",
    },
    {
      field: "StudentDetails.section",
      headerName: "Section",
      width: 80,
      editable: false,
      valueGetter: (params) =>
        params.row.division ||
        params.row.section ||
        params.row.Section ||
        params.row.StudentDetails?.section ||
        "",
    },
    {
      field: "EmiratesName",
      headerName: "Emirates",
      width: 100,
      editable: false,
    },
    {
      field: "RouteName",
      headerName: "Route",
      width: 150,
      editable: false,
    },
    {
      field: "BustopName",
      headerName: "Bus Stop",
      width: 150,
      editable: false,
    },
    {
      field: "ServiceType",
      headerName: "Service Type",
      width: 150,
      valueGetter: (params) => getServiceTypeDescription(Number(params.row?.ServiceType)) || "",
    },
    {
      field: "Status",
      headerName: "Status",
      width: 130,
      renderCell: (params) => {
        const statusVal = params.row?.Status || "Pending";
        let color = "warning";
        if (statusVal === "Approved" || statusVal === "1") color = "success";
        if (statusVal === "Rejected" || statusVal === "2") color = "error";
        return <Chip label={statusVal} color={color} size="small" variant="outlined" />;
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 250,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={() => handleViewStudent(params.row)}
            startIcon={<ViewIcon />}
          >
            View
          </Button>
          <IconButton color="error" size="small" onClick={() => handlePrintPDF(params.row)}>
            <PdfIcon />
          </IconButton>
        </Stack>
      ),
    },
  ];

  const fetchStudents = async () => {
    try {
      const response = await customFetch("application/my-applications");
      console.log("🚀 ~ fetchStudents ~ response:", response);
      let list = [];
      if (Array.isArray(response)) {
        list = response;
      } else if (response && Array.isArray(response.data)) {
        list = response.data;
      } else if (response && Array.isArray(response.applications)) {
        list = response.applications;
      }
      setStudents(list);
    } catch (error) {
      console.error("Error fetching my applications:", error);
      setStudents([]);
    }
  };
  useEffect(() => {
    fetchStudents();
  }, []);

  // Filtering effect
  useEffect(() => {
    let result = students;

    // Status filtering
    if (statusFilter !== "all") {
      result = result.filter(
        (student) =>
          (student.Status || "").toLowerCase() === statusFilter.toLowerCase()
      );
    }

    setFilteredStudents(result);
  }, [students, statusFilter]);

  const handleViewStudent = (student) => {
    setSelectedStudent(student);
    setOpenViewDialog(true);
  };

  const handleCloseViewDialog = () => {
    setOpenViewDialog(false);
    setSelectedStudent(null);
  };

  const handleEditStudent = async (updatedStudent) => {
    try {
      // Refetch the entire students list to ensure you have the latest data
      await fetchStudents();
      // Close the dialog
      setOpenViewDialog(false);
      setSelectedStudent(null);
    } catch (error) {
      console.error("Error updating student:", error);
    }
  };

  const handleDeleteStudent = () => {
    // Implement delete functionality
    console.log("Delete student:", selectedStudent);
  };

  const getServiceTypeDescription = (serviceType) => {
    switch (serviceType) {
      case 1:
        return "New Request";
      case 2:
        return "Change Request";
      case 3:
        return "Cancel Request";
      default:
        return "Unknown";
    }
  };

  const handlePrintPDF = async (student) => {
    const loadingToastId = toast.loading("Generating PDF... Please wait");

    try {
      const token = localStorage.getItem("token");
      const API_BASE_URL =
        process.env.REACT_APP_BASE_API_URL ||
        process.env.REACT_APP_BASE_API ||
        "http://localhost:8000/api/";
      const headers = {};
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const response = await fetch(
        `${API_BASE_URL}transportpdfandsend/pdf/${student.id}`,
        {
          method: "GET",
          headers,
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Failed to download PDF");
      }

      const studentNameVal =
        student.studentName ||
        student.StudentName ||
        student.MemSName ||
        student.MemFName ||
        student.StudentDetails?.MemSName ||
        student.StudentDetails?.MemFName ||
        student.StudentDetails?.StudentName ||
        student.StudentDetails?.studentName ||
        "";

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      const fileName = studentNameVal
        ? `${studentNameVal.replace(/[^a-zA-Z0-9_-]/g, "_")}_transport_form.pdf`
        : (student.RollNo ? `${student.RollNo}_transport_form.pdf` : "transport_form.pdf");
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);


      toast.update(loadingToastId, {
        render: "PDF downloaded successfully",
        type: "success",
        isLoading: false,
        autoClose: 3000,
      });
    } catch (error) {
      toast.update(loadingToastId, {
        render: `Failed to download PDF: ${error.message}`,
        type: "error",
        isLoading: false,
        autoClose: 3000,
      });
    }
  };

  return (
    <Box sx={{ width: "100%", height: 600 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h5" fontWeight="bold">My Requests</Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          {/* Status Filter */}
          <FormControl variant="outlined" size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status Filter</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="Status Filter"
            >
              <MenuItem value="all">All Statuses</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Box>

      <DataGrid
        rows={filteredStudents}
        columns={columns}
        pageSize={10}
        rowsPerPageOptions={[10, 25, 50]}
        disableSelectionOnClick
      />

      <StudentDetailsDialog
        open={openViewDialog}
        onClose={() => {
          setOpenViewDialog(false);
          setSelectedStudent(null);
        }}
        student={selectedStudent}
        onSave={handleEditStudent}
      />
    </Box>
  );
};

export default StudentManagement;
